import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Send,
  User,
  Building2,
  Info,
  Loader2,
  Clock,
  AlertTriangle,
  CheckCircle,
  FileText
} from 'lucide-react';
import { Typewriter } from '../components/Typewriter';
import VoiceInput from '../components/VoiceInput';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  animate?: boolean;
}

interface PatientChart {
  patient_description?: string;
  admission_reason?: string;
  medical_surgical_history?: string;
}

const InterventionPage: React.FC = () => {
  const { patientId } = useParams<{ patientId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupCMode = searchParams.get('groupc') === '1';
  const selectedTask = (searchParams.get('task') || '').toLowerCase();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [patientName, setPatientName] = useState<string>('');
  const [patientChart, setPatientChart] = useState<PatientChart | null>(null);
  const [patientProposal, setPatientProposal] = useState<string>('');
  const [targetIntervention, setTargetIntervention] = useState<string>('');
  const [interventionStatus, setInterventionStatus] = useState<string>('hidden');
  const [chartDrawerOpen, setChartDrawerOpen] = useState(() => window.innerWidth >= 1024);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [timeLeft, setTimeLeft] = useState(600);
  const [finishingSession, setFinishingSession] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const timeoutFinishTriggeredRef = useRef(false);

  useEffect(() => {
    if (patientId) {
      startConversation();
    }
  }, [patientId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (timeLeft <= 0) return;
    if (timeLeft === 120) {
        alert("Reminder: 2 minutes remaining in this session.");
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);
  useEffect(() => {
    if (!sessionId || timeLeft > 0 || loading || finishingSession || timeoutFinishTriggeredRef.current) {
      return;
    }

    timeoutFinishTriggeredRef.current = true;
    void finishSession('timeout');
  }, [timeLeft, sessionId, loading, finishingSession]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const navigateToAssignedChart = () => {
    navigate(
      groupCMode
        ? `/patient-chart/${patientId}?task=${selectedTask || 'intervention'}&groupc=1`
        : `/patient-chart/${patientId}`
    );
  };

  const handleBackToChart = () => {
    if (timeLeft > 0) {
      if (interventionStatus === 'addressed') {
        alert('The patient has accepted the proposed intervention. Please use "End Session Now" to finish the session.');
      } else {
        alert('You cannot leave the intervention session early. Continue until the patient accepts the plan or the timer expires. Once the intervention is successful, you will see a clear "Success!" message and can then end the session.');
      }
      return;
    }
    navigateToAssignedChart();
  };

  const startConversation = async () => {
    try {
      const patientResponse = await apiClient.get(`/api/patients/${patientId}`);
      setPatientName(patientResponse.data.name);
      setPatientChart({
        patient_description: patientResponse.data.patient_description,
        admission_reason: patientResponse.data.admission_reason,
        medical_surgical_history: patientResponse.data.medical_surgical_history
      });
      setPatientProposal(patientResponse.data.original_proposal || '');
      setTargetIntervention(patientResponse.data.intervention || '');

      const sessionResponse = await apiClient.post(`/api/conversation/start`, {
        patient_id: patientId,
        model_name: 'gpt-4o',
        enable_intervention: true
      });
      setSessionId(sessionResponse.data.session_id);
    } catch (error) {
      console.error('Failed to start conversation:', error);
      alert('Failed to start conversation. Please try again.');
      if (groupCMode) {
        navigate('/group-c-selection');
      } else {
        navigate(`/patient-chart/${patientId}`);
      }
    } finally {
      setInitializing(false);
    }
  };

  const finishSession = async (endReason: 'manual' | 'timeout' = 'manual') => {
    if (!sessionId) return;
    setFinishingSession(true);
    try {
      await apiClient.post('/api/conversation/finish_intervention', {
        session_id: sessionId,
        end_reason: endReason,
      });
      if (endReason === 'timeout') {
        alert('Time limit reached. This intervention session has ended automatically.');
      }
      navigateToAssignedChart();
    } catch (error: any) {
      console.error('Failed to finish intervention:', error);
      const detail = error?.response?.data?.detail;
      if (endReason === 'manual' && detail?.message) {
        alert(detail.message);
        return;
      }
      if (endReason === 'timeout') {
        timeoutFinishTriggeredRef.current = false;
        alert('Time limit reached, but the session could not be closed automatically. Please wait while the system retries.');
        return;
      }
      alert('Failed to end the intervention session. Please try again.');
    } finally {
      setFinishingSession(false);
    }
  };

  const handleEndSession = async () => {
    if (interventionStatus !== 'addressed') {
      alert('You can only end the session early after the patient accepts the proposed intervention.');
      return;
    }
    if (window.confirm('Are you sure you want to end the session?')) {
      await finishSession('manual');
    }
  };

  const handleSendMessage = async () => {
    const text = inputMessage.trim();
    if (!text || !sessionId || timeLeft <= 0 || finishingSession) return;

    const userMessage: Message = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const assistantMessage: Message = {
        role: 'assistant',
        content: 'Thinking...',
        timestamp: new Date().toISOString(),
        animate: true
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Use non-streaming endpoint to get intervention status metadata
      const response = await apiClient.post('/api/conversation/message', {
        session_id: sessionId,
        message: text,
        use_tts: false,
        from_audio: false
      });

      const data = response.data;
      
      setMessages(prev => {
        const newMessages = [...prev];
        // Replace "Thinking..." with actual response
        newMessages[newMessages.length - 1] = {
            role: 'assistant',
            content: data.response,
            timestamp: new Date().toISOString(),
            animate: false
        };
        return newMessages;
      });

      if (data.intervention_status) {
          setInterventionStatus(data.intervention_status);
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
      setLoading(false);
      // Remove the "Thinking..." placeholder on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
        setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (initializing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="w-full px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToChart}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold">Intervention: {patientName}</h1>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-primary-foreground/10 px-3 py-1 rounded-md">
                <Clock className="h-4 w-4" />
                <span className={`font-mono font-medium ${timeLeft < 60 ? 'text-red-300' : ''}`}>
                {formatTime(timeLeft)}
                </span>
            </div>

          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className={`flex-1 flex flex-col m-2 transition-all duration-300 ${chartDrawerOpen ? 'lg:mr-[400px]' : 'mr-2'}`}>
          <Card className="flex-1 flex flex-col overflow-hidden shadow-lg">
            <div className="bg-blue-50 border-b border-blue-100 p-4">
                <h3 className="text-sm font-semibold text-blue-800 mb-2">Your Goal: Identify the key hidden concern, address it, and then guide the patient toward accepting the new proposal.</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-red-200 bg-red-50 rounded p-3 relative">
                        <Badge className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 font-normal">Original Expectation</Badge>
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <h4 className="font-bold text-red-900 text-xs uppercase tracking-tight">Patient's Initial Preference</h4>
                                <p className="text-sm text-red-800 mt-1 leading-tight">{patientProposal || 'The patient has a pre-existing belief or preference regarding their care.'}</p>
                            </div>
                        </div>
                    </div>
                    <div className="border border-green-200 bg-green-50 rounded p-3 relative">
                        <Badge className="absolute -top-2 -right-2 bg-green-500 hover:bg-green-600 font-normal">Target Goal</Badge>
                        <div className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <h4 className="font-bold text-green-900 text-xs uppercase tracking-tight">Clinical Target</h4>
                                <p className="text-sm text-green-800 mt-1 leading-tight">{targetIntervention || 'Persuade the patient toward the clinically appropriate next step.'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {messages.length === 0 ? (
                <div className="text-center text-muted-foreground mt-8">
                  Start the conversation by addressing the patient's concerns.
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex flex-col rounded-lg p-4 max-w-[85%] ${
                        message.role === 'user' ? 'bg-blue-100 self-end ml-auto' : 'bg-gray-100 self-start mr-auto'
                      }`}
                    >
                      <span className="text-xs font-semibold text-muted-foreground mb-1">
                        {message.role === 'user' ? 'You' : patientName}
                      </span>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            <Separator />
            
            {/* Success Banner */}
            {interventionStatus === 'addressed' ? (
                <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4" role="alert">
                    <p className="font-bold">Success!</p>
                    <p>The patient has accepted the new proposal. You can continue practical follow-up or end the session.</p>
                    <div className="mt-3">
                      <Button size="sm" onClick={handleEndSession} disabled={finishingSession}>
                        End Session Now
                      </Button>
                    </div>
                </div>
            ) : (
                <div className="bg-amber-50 border-l-4 border-amber-400 text-amber-800 p-4" role="status">
                    <p className="font-semibold">Session remains active until success or timeout.</p>
                    <p className="text-sm mt-1">You cannot end this intervention early unless the patient accepts the proposed intervention. Once the intervention is successful, you will see a clear "Success!" message and may click "End Session Now". Otherwise, the session will end automatically when the timer expires.</p>
                </div>
            )}

            <div className="p-4 bg-white">
               <div className="flex gap-2">
                    <VoiceInput 
                        onTranscription={(text) => setInputMessage(prev => prev ? `${prev} ${text}` : text)}
                        disabled={loading || finishingSession || timeLeft <= 0}
                    />
                    <Input
                    className="flex-1"
                    placeholder="Type your message here..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading || finishingSession || timeLeft <= 0}
                    />
                    <Button onClick={() => handleSendMessage()} disabled={!inputMessage.trim() || loading || finishingSession || timeLeft <= 0}>
                        <Send className="h-4 w-4 mr-2" />Send
                    </Button>
                </div>
            </div>
          </Card>
        </div>

        <div className={`fixed right-0 top-16 h-[calc(100vh-4rem)] w-full lg:w-[400px] bg-white shadow-2xl transition-all duration-300 transform overflow-y-auto ${chartDrawerOpen ? 'translate-x-0' : 'translate-x-full'} z-10`}>
          <div className="p-4 space-y-4">
            <h3 className="font-semibold text-lg">Patient Chart</h3>
            <Separator />

            {patientChart?.patient_description && (
                <Card className="border shadow-none">
                    <CardHeader className="p-3 pb-0 flex flex-row items-center gap-2">
                        <User className="h-4 w-4 text-blue-600" />
                        <CardTitle className="text-sm">Patient Description</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3">
                        <p className="text-xs whitespace-pre-wrap">{patientChart.patient_description}</p>
                    </CardContent>
                </Card>
            )}

            {patientChart?.admission_reason && (
                <Card className="border shadow-none">
                    <CardHeader className="p-3 pb-0 flex flex-row items-center gap-2">
                        <Building2 className="h-4 w-4 text-red-600" />
                        <CardTitle className="text-sm">Encounter Reason</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3">
                        <p className="text-xs whitespace-pre-wrap">{patientChart.admission_reason}</p>
                    </CardContent>
                </Card>
            )}

            {patientChart?.medical_surgical_history && (
                <Card className="border shadow-none">
                    <CardHeader className="p-3 pb-0 flex flex-row items-center gap-2">
                        <FileText className="h-4 w-4 text-purple-600" />
                        <CardTitle className="text-sm">Medical & Surgical History</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3">
                        <p className="text-xs whitespace-pre-wrap">{patientChart.medical_surgical_history}</p>
                    </CardContent>
                </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterventionPage;


